/**
 * File: useStopTimerDialogController.ts
 * Path: apps/admin/src/shared/hooks/useStopTimerDialogController.ts
 * Description: Stopping the timer: the minutes it ran (rounded up to a whole
 * minute, changeable), what it was for, and saving the time entry.
 */

import { useState, type FormEvent } from "react";
import { MAX_ENTRY_MINUTES, type TimeKind } from "@idk/config";
import { isEntryMinutes } from "../utils/effortFormUtils";
import type { RunningTimer } from "../utils/timerUtils";
import { useEffortMutations } from "./useEffortMutations";

export const useStopTimerDialogController = (timer: RunningTimer, onSaved: () => void) => {
  const { addTime } = useEffortMutations();
  const [minutes, setMinutes] = useState(() =>
    String(Math.min(MAX_ENTRY_MINUTES, Math.max(1, Math.ceil((Date.now() - timer.startedAt) / 60000)))),
  );
  const [kind, setKind] = useState<TimeKind | "">("");
  const [note, setNote] = useState("");

  const parsed = Number(minutes);
  const valid = isEntryMinutes(parsed) && kind !== "";

  const save = (event: FormEvent) => {
    event.preventDefault();
    if (!valid) return;
    addTime.mutate({ record: timer.record, minutes: parsed, kind, note }, { onSuccess: onSaved });
  };

  return {
    minutes,
    kind,
    note,
    /** The minutes to show in the description: 0 until they are a positive whole number. */
    shownMinutes: Number.isInteger(parsed) && parsed > 0 ? parsed : 0,
    minutesInvalid: !Number.isInteger(parsed) || parsed < 1,
    setMinutes,
    setKind: (value: string) => setKind(value as TimeKind),
    setNote,
    save,
    canSave: valid && !addTime.isPending,
    saving: addTime.isPending,
  };
};
